import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { getFormularioCmdrsByUUID } from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { FormCmdrsHorizontalStepper } from 'components/forms/form_cmdrs_horizontal_stepper';
import { Loading } from 'components/login';
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import Step1CmdrsView from './steps/step1_view';
import Step2CmdrsView from './steps/step2_view';
import Step3CmdrsView from './steps/step3_view';
import Step4CmdrsView from './steps/step4_view';
import Step5CmdrsView from './steps/step5_view';
import { formatDate } from 'utils/format_date';
import { getListUsersByFilters } from 'api/user/user.api';
import { StepsVisibleForm } from 'components/forms/steps-visible-form';

export async function fetchCmdrsFormData(uuid: string) {
  const data = await getFormularioCmdrsByUUID(uuid);
  const currentYear = new Date().getFullYear();

  const normalizeDemands = (data.demands ?? []).map((d: any, idx: number) => {
    let year = Number(d.year ?? d.ano ?? null);

    // Se o backend não retorna 'ano', tenta recuperar do localStorage
    if (!year || year === 0) {
      const savedDemandsYears = localStorage.getItem(`cmdrs_demands_years_${uuid}`);
      if (savedDemandsYears) {
        const yearsMap = JSON.parse(savedDemandsYears);
        year = Number(yearsMap[idx]) || currentYear;
      } else {
        year = currentYear;
      }
    }

    return {
      object: d.object ?? d.objeto ?? '',
      quantity: Number(d.quantity ?? d.quantidade ?? 0),
      justification: d.justification ?? d.justificativa ?? '',
      year: year,
    };
  });

  return {
    step1: {
      hasCmdrs: data.has_cmdrs ?? null,

      instrumentNumber: data.instrument_number ?? '',
      publicationDate: formatDate(data.publication_date),

      status: data.status ?? '',
      structure: data.structure ?? '',
      presidingInstitution: data.presiding_institution ?? '',
      mandateEnd: formatDate(data.mandate_end),
      document_uuid: data.document_uuid ?? '',
      qtdPoderPublico: data.qtd_poder_publico ?? 0,
      instrument: data.instrument ?? '',
      qtdSociedadeCivil: data.qtd_sociedade_civil ?? 0,
    },
    step2: {
      members: data.members ?? [],
    },
    step3: {
      existingCouncils: data.existing_councils ?? [],
      seatedCouncils: data.seated_councils ?? [],
      otherExistingCouncil: data.other_existing_council ?? '',
      otherSeatedCouncil: data.other_seated_council ?? '',
      activitiesDescription: data.activities_description ?? '',
      isConsortiumMember: typeof data.is_consortium_member === 'boolean' ? data.is_consortium_member : null,
      participatesActivities: typeof data.participates_activities === 'boolean' ? data.participates_activities : null,

      hasSusaf: typeof data.has_susaf === 'boolean' ? data.has_susaf : null,
    },
    step4: {
      selectedChallenges: data.selected_challenges ?? [],
      otherChallenge: data.other_challenge ?? '',
      challengesOrder: data.challenges_order ?? [],
    },
    step5: {
      demands: normalizeDemands
    },
    created_by: data.created_by ?? '',
  };
}

export const AccessDenied: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Stack
      sx={{
        marginX: '4rem',
        paddingTop: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        backgroundColor: '#FFF',
        borderRadius: '1rem',
        padding: '2rem',
        margin: '2rem',
      }}
    >
      <Typography variant="h6" color="error">
        Acesso Negado
      </Typography>
      <Typography>Você não tem permissão para visualizar este formulário</Typography>
      <Button variant="contained" onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}>
        Voltar
      </Button>
    </Stack>
  );
};

export async function fetchAndValidateFormulario(uuid: string) {
  if (!uuid) throw new Error('UUID do formulário não encontrado');
  const formData = await fetchCmdrsFormData(uuid);

  const temDadosPreenchidos = Object.values(formData).some((value: any) =>
    value !== null && value !== undefined && value !== '' &&
    (Array.isArray(value) ? value.length > 0 : true),
  );

  if (!temDadosPreenchidos) {
    throw new Error('Nenhum dado preenchido disponível para este município');
  }

  return formData;
}

export const useFormularioVisualizar = (uuid?: string | undefined) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formulario, setFormulario] = useState<any>(null);
  const [responsible, setResponsible] = useState({
    responsibleName: '',
    responsibleRole: '',
    responsibleContact: '',
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setHasError(false);
        setErrorMessage('');
        if (!uuid) {
          setHasError(true);
          setErrorMessage('UUID do formulário não encontrado');
          return;
        }

        const data = await fetchAndValidateFormulario(uuid);
        if (!mounted) return;
        setFormulario(data);
      } catch (err: any) {
        if (!mounted) return;
        console.error('Erro ao buscar formulário:', err);
        setHasError(true);
        setErrorMessage(err.message || 'Município sem informação enviada para visualização pelo Gerente Estadual');
      }
    };

    load();

    return () => {
      mounted = false;
      setFormulario(null);
    };
  }, [uuid]);

  useEffect(() => {
    const responsibleUuid = formulario?.created_by;
    if (responsibleUuid) {
      getListUsersByFilters({ limit: '1', uuid: responsibleUuid }).then((res: any) => {
        const user = res?.data?.[0];
        setResponsible({
          responsibleName: user?.name || '',
          responsibleRole: user?.position || user?.occupation || '',
          responsibleContact: user?.contactList?.[0]?.value || '',
        });
      }).catch(() => { });
    }
  }, [formulario?.created_by]);

  return {
    currentStep,
    setCurrentStep,
    hasError,
    errorMessage,
    formulario,
    responsible,
  };
};

const FormularioCMDRSVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    currentStep,
    setCurrentStep,
    hasError,
    errorMessage,
    formulario,
    responsible,
  } = useFormularioVisualizar(uuid);

  const usuariosPermitidos = [
    UserPermissionEnum.STATE_MANAGER,
    UserPermissionEnum.MUNICIPAL_MANAGER,
    UserPermissionEnum.ADMIN,
  ];

  const isUsuarioPermitido = usuariosPermitidos.includes(
    user?.permission.name as UserPermissionEnum,
  );



  const renderStepContent = () => {
    if (hasError) {
      return (
        <Box justifyContent="center" display="flex" mt={3}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="body1">
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (!formulario) {
      return (
        <Box display="flex" justifyContent="center">
          <Loading />
        </Box>
      );
    }

    switch (currentStep) {
      case 1:
        return <Step1CmdrsView data={formulario.step1} />;
      case 2:
        return <Step2CmdrsView data={formulario.step2} />;
      case 3:
        return <Step3CmdrsView data={formulario.step3} />;
      case 4:
        return <Step4CmdrsView data={formulario.step4} />;
      case 5:
        return <Step5CmdrsView data={formulario.step5} />;
      default:
        return <Step1CmdrsView data={formulario.step1} />;
    }
  };

  return (
    <ContainerPainel
      pagina='Formulário do CMDRS'
      showBackButton={true}
    >
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '24px',
          lineHeight: '133%',
          letterSpacing: '0px',
          textAlign: 'center',
          verticalAlign: 'middle',
          mt: '63px',
          mb: '32px',
        }}
      >
        Visualizar Formulário do CMDRS
      </Typography>

      {isUsuarioPermitido ? (
        <Stack marginX={'4rem'} paddingTop={'2rem'}>
          <StepsVisibleForm
            activeStep={currentStep}
            steps={[
              'INF. SOBRE O CMDRS',
              'MEMBROS',
              'INF. COMPLEMENTARES',
              'DESAFIOS DA AGRICULTURA FAMILIAR',
              'DEMANDAS DA AGRICULTURA FAMILIAR',
            ]}
            onStepChange={setCurrentStep}
          />

          <Box mt={2}>{renderStepContent()}</Box>

          {/* Navegação entre abas */}
          {!hasError && formulario && (
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Button
                variant="text"
                onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
                sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
              >
                VOLTAR
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <Stack
          sx={{
            marginX: '4rem',
            paddingTop: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#FFF',
            borderRadius: '1rem',
            padding: '2rem',
            margin: '2rem',
          }}
        >
          <Typography variant="h6" color="error">
            Acesso Negado
          </Typography>
          <Typography>
            Você não tem permissão para visualizar este formulário
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
          >
            Voltar
          </Button>
        </Stack>
      )}
    </ContainerPainel>
  );
};

export default FormularioCMDRSVisualizar;
